import React, { useEffect, useState } from 'react';
import CloseIconWrapper from '@apps/components/CloseIconWrapper';
import CheckboxFilter from '@apps/components/Filter/Checkbox';
import RangeSliderComponent from '@apps/components/Filter/PriceRange';
import QuickFilterButton from '@apps/components/QuickFilterButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  Stack,
} from '@mui/material';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { isEmpty, isNumber, isString, omit } from 'lodash';
import isArray from 'lodash/isArray';

import { FilterProps } from './props';
import { Filter, useFilterValues } from './settings';

/**
 * ===========================
 * MAIN
 * ===========================
 */

export const FilterDialog: React.FC<FilterProps> = (props) => {
  const { open, onClose, settings, value, onSubmit, onReset } = props;

  // ======================= HOOKS
  useEffect(() => {
    setSelectedValues(value ?? {});
  }, [value]);

  const filterValues = useFilterValues({
    ...settings,
  });

  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: Array<string | number> | string | number | undefined;
  }>(value ?? {});

  const [allSelected, setAllSelected] = useState<{ [key: string]: boolean }>(
    {}
  );

  const finalSelectedValues = Object.keys(selectedValues).reduce(
    (acc, filterType) => {
      const currentValues = selectedValues[filterType] as string[];
      const allValues =
        filterValues
          .find((filter) => filter.key === filterType)
          ?.options?.map((v) => v.label) || [];

      if (allSelected[filterType]) {
        acc[filterType] = allValues.filter((v) => v !== 'All');
      } else {
        acc[filterType] = currentValues;
      }

      return acc;
    },
    {} as { [key: string]: string[] }
  );

  const { md } = useGetResponsive();

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleChange =
    (index: number) =>
    (event: React.ChangeEvent<unknown>, isExpanded: boolean) => {
      setExpandedIndex(isExpanded ? index : null);
    };

  const handleReset = () => {
    setExpandedIndex(null);
    setSelectedValues({});
    setAllSelected({});
    onReset?.();
  };

  // ======================= EVENTS

  // ======================= STATE

  // ======================= API

  // ======================= EVENTS

  const handleApply = () => {
    setExpandedIndex(null);
    onSubmit?.({ ...finalSelectedValues });
  };

  // ======================= VARIABLES
  const formatter = new Intl.NumberFormat('en-US');

  // ======================= RENDER FUNCTIONS
  const renderFilter = (filter: Filter) => {
    const isAllSelected = allSelected[filter.key];
    const isListFiltered = isArray(finalSelectedValues[filter.key]);

    if (isEmpty(finalSelectedValues[filter.key])) return null;

    if (isAllSelected) {
      return (
        <QuickFilterButton
          label="All"
          selected
          borderRadius="4px"
          sx={{
            height: '30px',
            '& .MuiChip-label': {
              display: 'flex',
            },
          }}
          onDelete={() => {
            setSelectedValues((prev) => ({
              ...prev,
              [filter.key]: undefined,
            }));
          }}
        />
      );
    }

    // List Filtered Value
    if (isListFiltered) {
      switch (filter.mode) {
        case 'slider':
          return (
            <QuickFilterButton
              label={
                `RM ${formatter.format(
                  finalSelectedValues[filter.key]?.[0] as unknown as number
                )}` +
                ' - ' +
                `RM ${formatter.format(
                  finalSelectedValues[filter.key]?.[1] as unknown as number
                )}`
              }
              selected
              borderRadius="4px"
              sx={{
                height: '30px',
                '& .MuiChip-label': {
                  display: 'flex',
                },
              }}
              onDelete={() => {
                setSelectedValues((prevState) => {
                  return {
                    ...prevState,
                    [filter.key]: undefined,
                  };
                });
              }}
            />
          );
        case 'checkbox':
        default:
          return finalSelectedValues[filter.key]?.map((value, chipIndex) => {
            return (
              ((isNumber(value) && value !== 0) || isString(value)) && (
                <QuickFilterButton
                  key={chipIndex}
                  label={
                    filter?.options.find((option) => option.value === value)
                      ?.label
                  }
                  selected
                  borderRadius="4px"
                  sx={{
                    height: '30px',
                    '& .MuiChip-label': {
                      display: 'flex',
                    },
                  }}
                  onDelete={() => {
                    setSelectedValues((prevState) => {
                      const currentValues = prevState[filter.key];
                      return {
                        ...prevState,
                        [filter.key]: isArray(currentValues)
                          ? currentValues?.filter((item) => item !== value)
                          : undefined,
                      };
                    });
                  }}
                />
              )
            );
          });
      }
    }

    // Single Filtered Value
    return (
      <QuickFilterButton
        label={finalSelectedValues[filter.key]}
        variant="outlined"
        selected={false}
        borderRadius="4px"
        sx={{
          minHeight: '30px',
        }}
        onDelete={() => {
          setSelectedValues((prevState) => {
            return {
              ...prevState,
              [filter.key]: undefined,
            };
          });
        }}
      />
    );
  };

  // ======================= VIEWS
  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setExpandedIndex(null);
      }}
      maxWidth="md"
      fullScreen={md}
      sx={{
        zIndex: 1301,
        '& .MuiDialog-paper': {
          borderRadius: { xs: 0, md: '4px' },
          border: 'none',
        },
      }}
    >
      <CloseIconWrapper
        title={'Filter Result'}
        onClose={() => {
          onClose();
          setExpandedIndex(null);
        }}
        footerButton={{
          primary: {
            label: 'Apply',
            onClick: handleApply,
          },
          secondary: {
            label: 'Reset',
            onClick: handleReset,
          },
        }}
        contentProps={{
          sx: {
            p: 0,
          },
        }}
      >
        {/* Filter Content */}
        {filterValues?.map((filter, index) => {
          const show = expandedIndex !== index;
          const isLastItem = filterValues?.length === index + 1;
          const isExpanded = expandedIndex === index;
          const ExpandedIcon = isExpanded ? ExpandLessIcon : ExpandMoreIcon;
          return (
            <>
              <Accordion
                expanded={isExpanded}
                onChange={handleChange(index)}
                key={index}
                disableGutters
                sx={{
                  boxShadow: 'none',
                  borderTop: '0 !important',
                  borderRight: 0,
                  borderLeft: 0,
                  '&:before': {
                    display: 'none',
                  },
                  borderRadius: '0px',
                  overflowX: 'hidden',
                  padding: '20px',
                  ...(isLastItem && { borderBottom: '0 !important' }),
                }}
              >
                <AccordionSummary
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    padding: '0px',
                    margin: '-12px 0px',
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '24px',
                  }}
                >
                  <Stack direction="column" gap={2} sx={{ width: '100%' }}>
                    {/* Filter Title */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <div>{filter.title}</div>
                      <ExpandedIcon
                        style={{
                          color: '#1E242F',
                        }}
                      />
                    </Stack>
                    {show && (
                      <Stack direction="row" flexWrap="wrap" sx={{ gap: 3 }}>
                        {renderFilter(filter)}
                      </Stack>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: '0px' }}>
                  {filter.mode === 'slider' && (
                    <RangeSliderComponent
                      onChange={(event) => {
                        setSelectedValues((prev) => ({
                          ...prev,
                          [filter.key]: event,
                        }));
                      }}
                      options={
                        filter?.options as { label: string; value: number }[]
                      }
                      value={value?.[filter?.key]}
                      containerProps={{
                        sx: { py: 0, px: 1, my: 1 },
                        spacing: { xs: 0, md: 2, lg: 3 },
                      }}
                    />
                  )}
                  {filter.mode === 'checkbox' && (
                    <CheckboxFilter
                      onChange={(event) => {
                        if (isArray(event) && event.length === 0) {
                          setSelectedValues((prev) => ({
                            ...omit(prev, filter.key),
                          }));
                        } else {
                          setSelectedValues((prev) => ({
                            ...prev,
                            [filter.key]: event,
                          }));
                        }
                      }}
                      options={
                        filter?.options as { label: string; value: string }[]
                      }
                      GridProps={{
                        xs: 6,
                      }}
                      containerProps={{ sx: { paddingY: '5px' } }}
                      withIcon={filter.key === 'body_type'}
                      value={value?.[filter?.key]}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          );
        })}
      </CloseIconWrapper>
    </Dialog>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default FilterDialog;
